/* eslint-disable @typescript-eslint/no-explicit-any */
import { getErrorMessage } from '@juvare/common-js/utils';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import React from 'react';
/**
 * Form config for disabling components. Supports conditional logic based on sibling components.
 * Very similar to hidden form configuration, but with logic for disabling components, so any changes here should likely be reflected in the hidden form config.
 * */
export function getDisableFormConfig(params) {
    const { t, siblingCheckboxComponents, siblingNumberComponents, siblingRadioComponents, siblingSelectComponents, siblingRadioConfigs, siblingSelectConfigs, siblingTextComponents, siblingYesNoComponents, selectComponentValues, handleUpdateAnyValuesBasedOnSiblingConfig: onUpdateAnyValuesBasedOnSiblingConfig, } = params;
    return [
        {
            type: 'form.panel',
            key: 'disabledConfig',
            children: [
                {
                    type: 'form.content',
                    html: `<h3>${t('common.disabled')}</h3>`,
                },
                {
                    type: 'form.radio',
                    key: 'type',
                    values: [
                        { value: 'enabled', label: t('buildHiddenTab.disableConfig.enabled') },
                        { value: 'disabled', label: t('buildHiddenTab.disableConfig.disabled') },
                        { value: 'disable', label: t('buildHiddenTab.disableConfig.disable') },
                        { value: 'enable', label: t('buildHiddenTab.disableConfig.enable') },
                        { value: 'custom', label: t('buildHiddenTab.disableConfig.custom') },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.panel',
                    hidden: {
                        custom: (handler) => {
                            var _a;
                            const when = (_a = handler.up().down('type')) === null || _a === void 0 ? void 0 : _a.getData();
                            return when !== 'disable' && when !== 'enable';
                        },
                    },
                    children: [
                        {
                            type: 'form.select',
                            key: 'component',
                            label: t('buildHiddenTab.whenComponent'),
                            values: selectComponentValues,
                            hidden: {
                                when: '^type',
                                dataEq: 'enabled',
                            },
                        },
                        {
                            type: 'form.radio',
                            key: 'dataEqCheckbox',
                            label: t('buildHiddenTab.checkbox'),
                            values: [
                                { value: true, label: t('buildHiddenTab.checked') },
                                { value: false, label: t('buildHiddenTab.notChecked') },
                            ],
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingCheckboxComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.number',
                            key: 'dataEqNumber',
                            label: t('buildHiddenTab.number'),
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingNumberComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.yesNo',
                            key: 'dataEqYesNo',
                            label: t('buildHiddenTab.yesNo'),
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingYesNoComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'dataEqText',
                            label: t('buildHiddenTab.textEquals'),
                            multiline: true,
                            rows: 2,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingTextComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.checkboxList',
                            key: 'dataInCheckboxList',
                            label: t('buildHiddenTab.valueIsOneOf'),
                            allowInvalidValues: true,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingRadioComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                            event: {
                                onUpdateAny: (handler) => {
                                    var _a;
                                    return onUpdateAnyValuesBasedOnSiblingConfig(handler, siblingRadioConfigs[(_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()]);
                                },
                            },
                        },
                        {
                            type: 'form.multiSelect',
                            key: 'dataInMultiSelect',
                            label: t('buildHiddenTab.valueIsOneOf'),
                            allowInvalidValues: true,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingSelectComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                            event: {
                                onUpdateAny: (handler) => {
                                    var _a;
                                    return onUpdateAnyValuesBasedOnSiblingConfig(handler, siblingSelectConfigs[(_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()]);
                                },
                            },
                        },
                        {
                            type: 'form.panel',
                            hidden: {
                                custom: (handler) => { var _a; return ((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()) !== '$'; },
                            },
                            children: [
                                {
                                    type: 'form.text',
                                    key: 'when',
                                    label: t('buildHiddenTab.componentQuery'),
                                    validate: {
                                        required: true,
                                    },
                                },
                                {
                                    type: 'form.radio',
                                    key: 'whenDataEqType',
                                    label: t('buildHiddenTab.valueEquals'),
                                    values: [
                                        { value: 'text', label: t('buildHiddenTab.specificText') },
                                        { value: 'number', label: t('buildHiddenTab.specificNumber') },
                                        { value: 'true', label: t('common.true') },
                                        { value: 'false', label: t('common.false') },
                                        { value: 'eqJson', label: t('buildHiddenTab.jsonValue') },
                                        { value: 'inJson', label: t('buildHiddenTab.oneOfJsonValues') },
                                    ],
                                    style: {
                                        inline: true,
                                    },
                                },
                                {
                                    type: 'form.number',
                                    key: 'whenDataEqNumber',
                                    label: t('buildHiddenTab.numberLabel'),
                                    validate: {
                                        required: true,
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'number'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataEqText',
                                    label: t('buildHiddenTab.textLabel'),
                                    multiline: true,
                                    rows: 2,
                                    validate: {
                                        required: true,
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'text'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataEqJson',
                                    label: t('buildHiddenTab.jsonLabel'),
                                    multiline: true,
                                    rows: 2,
                                    validate: {
                                        required: true,
                                        custom: (value) => {
                                            if (value) {
                                                try {
                                                    JSON.parse(value);
                                                }
                                                catch (e) {
                                                    return e.toString();
                                                }
                                            }
                                            return undefined;
                                        },
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'eqJson'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataInJson',
                                    label: t('buildHiddenTab.jsonArray'),
                                    multiline: true,
                                    rows: 6,
                                    validate: {
                                        required: true,
                                        custom: (value) => {
                                            if (value) {
                                                try {
                                                    if (Array.isArray(JSON.parse(value))) {
                                                        return undefined;
                                                    }
                                                    else {
                                                        return t('buildHiddenTab.jsonArrayExpected');
                                                    }
                                                }
                                                catch (e) {
                                                    return e.toString();
                                                }
                                            }
                                            return undefined;
                                        },
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'inJson'; },
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'form.panel',
                    hidden: {
                        custom: (handler) => {
                            var _a;
                            const when = (_a = handler.up().down('type')) === null || _a === void 0 ? void 0 : _a.getData();
                            return when !== 'custom';
                        },
                    },
                    children: [
                        {
                            type: 'form.text',
                            key: 'custom',
                            label: t('buildHiddenTab.customJsFunction'),
                            emptyAsNull: true,
                            multiline: true,
                            rows: 6,
                            style: {
                                monospace: true,
                            },
                            validate: {
                                required: true,
                                custom: (value) => {
                                    if (value) {
                                        try {
                                            Function('"use strict"; return (function(handler) { ' + value + '; })')();
                                        }
                                        catch (error) {
                                            return getErrorMessage(error);
                                        }
                                    }
                                    return undefined;
                                },
                            },
                        },
                        {
                            type: 'form.button',
                            label: t('buildHiddenTab.showExample'),
                            event: {
                                onClick: () => {
                                    showAlertDialog({
                                        title: t('buildHiddenTab.customDisabledFunctionExample'),
                                        content: React.createElement("pre", { style: { margin: 0 } }, exampleDisabledCustomJavaScript),
                                    });
                                },
                            },
                            style: {
                                align: 'right',
                            },
                        },
                    ],
                },
            ],
        },
        {
            type: 'form.checkbox',
            key: 'clearDataOnDisable',
            label: t('buildHiddenTab.clearDataOnDisable'),
            description: t('buildHiddenTab.clearDataOnDisableDescription'),
            hidden: {
                when: '^disabledConfig/type',
                dataEq: 'enabled',
            },
        },
    ];
}
const exampleDisabledCustomJavaScript = `// This code disables component based on combined value of two other components
const value1 = handler.up().down('component1')?.getData();
const value2 = handler.up().down('component2')?.getData();
return value1 === 'x' || value2 === 'y';
`;
